// src/store/authStore.ts — Zustand store de autenticación (POW-02)
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '@/services/api'
import type { PermisosMap } from '@/services/powApi'
import { normalizeApiDetail } from '@/utils/authGuards'

export interface AdminUser {
  id: number
  nombre: string
  correo: string
  rol: string
  es_superadmin?: boolean
  permisos?: PermisosMap
  avatar?: string
}

interface AuthState {
  user: AdminUser | null
  accessToken: string | null
  step: 'credentials' | '2fa' | 'authenticated'
  loading: boolean
  error: string | null
  tempCorreo: string | null
  intentosRestantes: number | null
  codigoInvalidado: boolean
  cuentaBloqueadaMinutos: number | null
  // LOGIN-15: el admin con contraseña temporal debe cambiarla antes de operar.
  requiereCambioPassword: boolean
  // Actions
  login: (correo: string, contrasena: string) => Promise<void>
  verify2FA: (otp: string) => Promise<void>
  resend2FA: () => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
  resetToCredentials: () => void
  limpiarCambioObligatorio: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      step: 'credentials',
      loading: false,
      error: null,
      tempCorreo: null,
      intentosRestantes: null,
      codigoInvalidado: false,
      cuentaBloqueadaMinutos: null,
      requiereCambioPassword: false,

      login: async (correo, contrasena) => {
        set({ loading: true, error: null, cuentaBloqueadaMinutos: null, codigoInvalidado: false, intentosRestantes: null })
        try {
          const { data } = await api.post('/api/auth/admin/login', { correo, contrasena })
          if (data.requiere_2fa) {
            set({ step: '2fa', tempCorreo: correo, loading: false })
          } else {
            const tokenData = data.token
            if (!tokenData?.access_token) {
              set({ error: 'Respuesta inválida del servidor.', loading: false })
              return
            }
            localStorage.setItem('pow_access_token', tokenData.access_token)
            localStorage.setItem('pow_refresh_token', tokenData.refresh_token)

            // Perfil real obligatorio: nunca fabricar identidad/rol (BUG-02).
            try {
              const { data: me } = await api.get('/api/auth/admin/me')
              if (!me?.id) throw new Error('perfil_incompleto')
              set({
                step: 'authenticated',
                user: {
                  id: Number(me.id),
                  nombre: me.nombre ?? correo,
                  correo: me.email ?? correo,
                  rol: me.rol ?? '',
                  es_superadmin: Boolean(me.es_superadmin),
                  permisos: me.permisos,
                },
                accessToken: tokenData.access_token,
                // BUG-07: el backend puede exigir el cambio de contraseña temporal.
                requiereCambioPassword: Boolean(data.requiere_cambio_contrasena),
                loading: false,
              })
            } catch {
              localStorage.removeItem('pow_access_token')
              localStorage.removeItem('pow_refresh_token')
              set({
                step: 'credentials',
                user: null,
                accessToken: null,
                error: 'No se pudo cargar tu perfil. Intenta de nuevo.',
                loading: false,
              })
            }
          }
        } catch (err: any) {
          let msg = 'Correo o contraseña incorrectos'
          let bloqueadoMins: number | null = null
          if (err.response?.data?.detail) {
            const detail = normalizeApiDetail(err.response.data.detail, msg)
            if (detail.startsWith('cuenta_bloqueada')) {
              bloqueadoMins = detail.includes(':') ? parseInt(detail.split(':')[1], 10) : 15
              msg = `Tu cuenta ha sido bloqueada temporalmente por 15 minutos debido a 5 intentos fallidos consecutivos. Por favor espera ${bloqueadoMins} minuto(s) o revisa tu correo.`
            } else if (detail === 'credenciales_invalidas') {
              msg = 'Credenciales inválidas. Verifica tu correo y contraseña.'
            } else if (detail === 'usuario_no_encontrado') {
              msg = 'Credenciales inválidas.'
            } else {
              msg = detail
            }
          }
          set({ error: msg, cuentaBloqueadaMinutos: bloqueadoMins, loading: false })
        }
      },

      verify2FA: async (otp) => {
        set({ loading: true, error: null })
        try {
          const correo = get().tempCorreo ?? ''
          const { data } = await api.post('/api/auth/admin/verify-2fa', { correo, codigo_otp: otp })
          
          const tokenData = data.token
          const perfil = data.user
          if (!tokenData?.access_token || !perfil?.id) {
            // Nunca autenticar con identidad fabricada (BUG-02).
            set({ error: 'Respuesta inválida del servidor.', loading: false })
            return
          }
          localStorage.setItem('pow_access_token', tokenData.access_token)
          localStorage.setItem('pow_refresh_token', tokenData.refresh_token)

          const loggedUser: AdminUser = {
            id: Number(perfil.id),
            nombre: perfil.nombre ?? correo,
            correo: perfil.correo ?? correo,
            rol: perfil.rol ?? '',
            es_superadmin: Boolean(perfil.es_superadmin),
            permisos: perfil.permisos,
          }
          
          set({
            step: 'authenticated',
            user: loggedUser,
            accessToken: tokenData.access_token,
            tempCorreo: null,
            intentosRestantes: null,
            codigoInvalidado: false,
            requiereCambioPassword: Boolean(data.requiere_cambio_contrasena),
            loading: false,
          })
        } catch (err: any) {
          let msg = 'Código de verificación incorrecto o expirado'
          let intentos: number | null = null
          let invalidado = false

          if (err.response?.data?.detail) {
            const detail = normalizeApiDetail(err.response.data.detail, msg)
            if (detail.startsWith('codigo_incorrecto')) {
              intentos = detail.includes(':') ? parseInt(detail.split(':')[1], 10) : 1
              msg = `Código incorrecto. Te quedan ${intentos} intento${intentos === 1 ? '' : 's'} restante${intentos === 1 ? '' : 's'}.`
            } else if (detail === 'codigo_invalidado') {
              invalidado = true
              msg = 'Código invalidado tras 3 intentos fallidos. Debes reiniciar el inicio de sesión.'
            } else if (detail === 'codigo_expirado') {
              msg = 'El código ha expirado (10 min). Por favor, solicita uno nuevo.'
            } else if (detail.startsWith('cuenta_bloqueada')) {
              msg = 'La cuenta está temporalmente bloqueada por seguridad.'
            } else {
              msg = detail
            }
          }
          set({
            error: msg,
            intentosRestantes: intentos,
            codigoInvalidado: invalidado,
            loading: false,
          })
        }
      },

      resend2FA: async () => {
        const correo = get().tempCorreo
        if (!correo) return false
        set({ loading: true, error: null })
        try {
          await api.post('/api/auth/admin/resend-2fa', { correo })
          set({ loading: false, intentosRestantes: null, codigoInvalidado: false })
          return true
        } catch (err: any) {
          let msg = 'Error al reenviar el código. Intenta nuevamente.'
          if (err.response?.data?.detail) {
            const detail = normalizeApiDetail(err.response.data.detail, msg)
            if (detail.startsWith('cooldown_activo')) {
              const seg = detail.includes(':') ? detail.split(':')[1] : '60'
              msg = `Debes esperar ${seg} segundos antes de solicitar un nuevo código.`
            } else {
              msg = detail
            }
          }
          set({ error: msg, loading: false })
          return false
        }
      },

      logout: async () => {
        try {
          if (localStorage.getItem('pow_access_token')) {
            await api.post('/api/auth/admin/logout')
          }
        } catch {
          // Si el token expiró o la red falla, continuar con la limpieza local
        } finally {
          localStorage.removeItem('pow_access_token')
          localStorage.removeItem('pow_refresh_token')
          localStorage.removeItem('pow_otp_session')
          localStorage.removeItem('pow-auth')
          set({
            user: null,
            accessToken: null,
            step: 'credentials',
            tempCorreo: null,
            error: null,
            intentosRestantes: null,
            codigoInvalidado: false,
            cuentaBloqueadaMinutos: null,
            requiereCambioPassword: false,
          })
        }
      },

      clearError: () => set({ error: null }),

      limpiarCambioObligatorio: () => set({ requiereCambioPassword: false }),

      resetToCredentials: () => {
        set({
          step: 'credentials',
          tempCorreo: null,
          error: null,
          intentosRestantes: null,
          codigoInvalidado: false,
          cuentaBloqueadaMinutos: null,
        })
      },
    }),
    {
      name: 'pow-auth',
      // BUG-07: `requiereCambioPassword` se persiste para que un F5 no oculte el modal.
      partialize: (s) => ({
        user: s.user,
        accessToken: s.accessToken,
        step: s.step,
        tempCorreo: s.tempCorreo,
        requiereCambioPassword: s.requiereCambioPassword,
      }),
      onRehydrateStorage: () => (state) => {
        const hasLocalToken = Boolean(localStorage.getItem('pow_access_token'))
        if (state && state.step === 'authenticated' && (!state.accessToken || !hasLocalToken)) {
          state.step = 'credentials'
          state.accessToken = null
          state.user = null
          state.requiereCambioPassword = false
          localStorage.removeItem('pow_access_token')
          localStorage.removeItem('pow_refresh_token')
          localStorage.removeItem('pow_otp_session')
        }
      },
    },
  ),
)
